/**
 * iter-360 — Sponsor Admin CRUD.
 *
 * Owner / Admin only. Route: /settings/sponsors.
 * Lists every sponsor campaign, shows live impressions/clicks/leads/CTR,
 * lets admins create + edit + toggle active + delete campaigns.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Edit3, Trash2, ExternalLink, Building2, BarChart3, Sparkles } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ZONES = [
  { v: 'dashboard', label: 'Dashboard' },
  { v: 'material_library', label: 'Material Library' },
  { v: 'boq_categories', label: 'BOQ Categories' },
  { v: 'ai_assistant', label: 'AI Assistant' },
  { v: 'vendor_marketplace', label: 'Vendor Marketplace' },
  { v: 'boq_edit', label: 'BOQ Edit page' },
  { v: 'boq_client_published', label: 'Client Published BOQ' },
  { v: 'boq_vendor_published', label: 'Vendor Published BOQ' },
];
const PACKAGES = ['monthly', 'category', 'project', 'featured', 'cost_per_lead'];
const KINDS = [
  { v: 'featured_brand_partner', label: 'Featured Brand Partner (default)' },
  { v: 'brand_spotlight', label: 'Brand Spotlight (library / marketplace)' },
  { v: 'featured_product_partner', label: 'Featured Product Partner' },
  { v: 'in_association_with', label: 'In Association With (project-level)' },
];
// iter-364 — CAT_CODES fallback removed. We now load the FULL live category
// list from /api/boq-categories/for-tasks so any new main-category added by
// the admin (K, L, M, …) automatically appears in the targeting checkboxes.

const empty = {
  brand_name: '', tagline: '', logo_url: '', banner_url: '',
  cta_label: 'View Catalogue', cta_url: '',
  placement_zones: [], category_codes: [],
  start_date: '', end_date: '', package_type: 'category',
  partnership_kind: 'featured_brand_partner', is_exclusive: false,
  weight: 10, is_active: true, notes: '',
};

export default function SponsorAdmin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  // iter-364 — live category list [{ letter, name }] fetched from BOQ Category Library
  const [catCatalog, setCatCatalog] = useState([]);
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const fetchCatCatalog = async () => {
    try {
      const r = await axios.get(`${API}/boq-categories/for-tasks`, { headers });
      const rows = r.data || [];
      const seen = new Map();
      rows.forEach((c) => {
        const letter = (c.group || c.code || '').trim().toUpperCase();
        const name = c.group_name || c.name || '';
        if (letter && !seen.has(letter)) seen.set(letter, name);
      });
      setCatCatalog([...seen.entries()].sort((a, b) => a[0].localeCompare(b[0]))
        .map(([letter, name]) => ({ letter, name })));
    } catch {
      // Fallback keeps the old A-J list if the endpoint isn't reachable.
      setCatCatalog('ABCDEFGHIJ'.split('').map(l => ({ letter: l, name: '' })));
    }
  };

  const fetchRows = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/sponsors`, { headers });
      setRows(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load sponsors');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchRows(); fetchCatCatalog(); }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setDialogOpen(true); };
  const openEdit = (r) => {
    setEditing(r.id);
    setForm({
      ...empty, ...r,
      start_date: (r.start_date || '').slice(0, 10),
      end_date: (r.end_date || '').slice(0, 10),
    });
    setDialogOpen(true);
  };
  const toggleZone = (v) => setForm(f => ({ ...f, placement_zones: f.placement_zones.includes(v) ? f.placement_zones.filter(z => z !== v) : [...f.placement_zones, v] }));
  const toggleCat = (v) => setForm(f => ({ ...f, category_codes: f.category_codes.includes(v) ? f.category_codes.filter(c => c !== v) : [...f.category_codes, v] }));

  const save = async () => {
    if (!form.brand_name.trim() || !form.cta_url.trim()) { toast.error('Brand name + CTA URL are required'); return; }
    if (form.placement_zones.length === 0) { toast.error('Pick at least one placement zone'); return; }
    try {
      if (editing) {
        await axios.patch(`${API}/sponsors/${editing}`, form, { headers });
        toast.success('Sponsor updated');
      } else {
        await axios.post(`${API}/sponsors`, form, { headers });
        toast.success('Sponsor created');
      }
      setDialogOpen(false);
      fetchRows();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete sponsor "${name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/sponsors/${id}`, { headers });
      toast.success('Deleted');
      fetchRows();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const totals = rows.reduce((a, r) => ({
    imp: a.imp + (r.impressions || 0),
    clk: a.clk + (r.clicks || 0),
    lds: a.lds + (r.leads || 0),
  }), { imp: 0, clk: 0, lds: 0 });

  return (
    <Layout>
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-stone-900 flex items-center gap-2">
              <Sparkles size={20} className="text-amber-600" /> Sponsored Brand Placements
            </h1>
            <p className="text-sm text-stone-500 mt-1">
              Sell brand placements to vendors (Häfele, Hettich, Merino, Century, etc.). Every placement carries
              a mandatory &quot;Sponsored&quot; label. <strong>AI cost / rate / recommendation prompts never see sponsor data</strong>.
            </p>
          </div>
          <Button onClick={openCreate} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="sponsor-new-btn">
            <Plus size={14} className="mr-1" /> New Campaign
          </Button>
        </div>

        <div className="grid grid-cols-4 gap-3">
          <div className="bg-white border border-stone-200 rounded-lg p-4">
            <div className="text-xs uppercase tracking-wider text-stone-500">Total Campaigns</div>
            <div className="text-2xl font-bold text-stone-900 mt-1">{rows.length}</div>
          </div>
          <div className="bg-white border border-stone-200 rounded-lg p-4">
            <div className="text-xs uppercase tracking-wider text-stone-500">Impressions</div>
            <div className="text-2xl font-bold text-stone-900 mt-1">{totals.imp.toLocaleString('en-IN')}</div>
          </div>
          <div className="bg-white border border-stone-200 rounded-lg p-4">
            <div className="text-xs uppercase tracking-wider text-stone-500">Clicks</div>
            <div className="text-2xl font-bold text-stone-900 mt-1">{totals.clk.toLocaleString('en-IN')}</div>
            <div className="text-[10px] text-stone-400 mt-0.5">CTR {totals.imp ? ((totals.clk / totals.imp) * 100).toFixed(1) : 0}%</div>
          </div>
          <div className="bg-white border border-stone-200 rounded-lg p-4">
            <div className="text-xs uppercase tracking-wider text-stone-500">Leads</div>
            <div className="text-2xl font-bold text-stone-900 mt-1">{totals.lds.toLocaleString('en-IN')}</div>
          </div>
        </div>

        <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="sponsor-table">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr className="text-left text-xs uppercase tracking-wider text-stone-500">
                <th className="px-4 py-3">Brand</th>
                <th className="px-4 py-3">Placement</th>
                <th className="px-4 py-3">Categories</th>
                <th className="px-4 py-3 text-right">Impr.</th>
                <th className="px-4 py-3 text-right">Clicks</th>
                <th className="px-4 py-3 text-right">CTR</th>
                <th className="px-4 py-3 text-right">Leads</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={9} className="p-6 text-center text-stone-500">Loading…</td></tr>)}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={9} className="p-8 text-center text-stone-500">No sponsor campaigns yet. Click <strong>New Campaign</strong> to add one.</td></tr>
              )}
              {rows.map((r) => {
                const ctr = r.impressions ? ((r.clicks / r.impressions) * 100).toFixed(1) : '0.0';
                return (
                  <tr key={r.id} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`sponsor-row-${r.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {r.logo_url ? (
                          <img src={r.logo_url} alt={r.brand_name} className="w-8 h-8 object-contain" />
                        ) : (
                          <div className="w-8 h-8 rounded bg-amber-100 flex items-center justify-center font-bold text-amber-800 text-xs">{r.brand_name.charAt(0)}</div>
                        )}
                        <div>
                          <div className="font-semibold text-stone-900">{r.brand_name}</div>
                          <div className="text-[10px] text-stone-500">{r.package_type}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(r.placement_zones || []).map(z => (
                          <Badge key={z} variant="secondary" className="text-[10px] bg-stone-100 text-stone-700">{z}</Badge>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-stone-600">{(r.category_codes || []).join(', ') || 'All'}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs">{r.impressions || 0}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs">{r.clicks || 0}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs text-emerald-700">{ctr}%</td>
                    <td className="px-4 py-3 text-right font-mono text-xs">{r.leads || 0}</td>
                    <td className="px-4 py-3">
                      {r.is_live_now ? (
                        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">Live</Badge>
                      ) : r.is_active ? (
                        <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px]">Scheduled</Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-stone-100 text-stone-500 text-[10px]">Paused</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {r.cta_url && (
                          <a href={r.cta_url} target="_blank" rel="noopener noreferrer" className="text-stone-400 hover:text-stone-700" title="Open destination"><ExternalLink size={13} /></a>
                        )}
                        {(r.leads || 0) > 0 && (
                          <a
                            href={`${API}/sponsors/${r.id}/leads.csv`}
                            className="text-emerald-600 hover:text-emerald-800 text-[10px] font-semibold px-1.5"
                            title={`Download ${r.leads} lead(s) as CSV`}
                            data-testid={`sponsor-leads-csv-${r.id}`}
                            download
                          >CSV</a>
                        )}
                        <RouterLink
                          to={`/settings/sponsors/${r.id}/analytics`}
                          className="text-blue-600 hover:text-blue-800"
                          title="View analytics"
                          data-testid={`sponsor-analytics-${r.id}`}
                        >
                          <BarChart3 size={13} />
                        </RouterLink>
                        <Button variant="ghost" size="sm" onClick={() => openEdit(r)} data-testid={`sponsor-edit-${r.id}`}><Edit3 size={13} /></Button>
                        <Button variant="ghost" size="sm" onClick={() => remove(r.id, r.brand_name)} className="text-red-500 hover:text-red-700" data-testid={`sponsor-del-${r.id}`}><Trash2 size={13} /></Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Create / Edit dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sponsor-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 size={16} className="text-amber-600" />
                {editing ? 'Edit Sponsor Campaign' : 'New Sponsor Campaign'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Brand Name *</Label>
                  <Input value={form.brand_name} onChange={(e) => setForm(f => ({ ...f, brand_name: e.target.value }))} placeholder="e.g. Häfele India" data-testid="sponsor-form-brand" />
                </div>
                <div>
                  <Label className="text-xs">Tagline</Label>
                  <Input value={form.tagline || ''} onChange={(e) => setForm(f => ({ ...f, tagline: e.target.value }))} placeholder="e.g. World-class hardware for kitchens" data-testid="sponsor-form-tagline" />
                </div>
                <div>
                  <Label className="text-xs">Logo URL</Label>
                  <Input value={form.logo_url || ''} onChange={(e) => setForm(f => ({ ...f, logo_url: e.target.value }))} placeholder="https://…/logo.png OR upload below" data-testid="sponsor-form-logo" />
                  <div className="mt-1 flex items-center gap-2">
                    <label className="inline-flex items-center gap-1 text-[11px] cursor-pointer px-2 py-1 rounded border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 font-medium">
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/svg+xml"
                        className="hidden"
                        data-testid="sponsor-form-logo-file"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          if (file.size > 1_000_000) {
                            toast.error('Logo file must be under 1 MB.');
                            e.target.value = '';
                            return;
                          }
                          // iter-362 — upload to object storage. Falls back to
                          // base64 embedding if the storage service is offline.
                          const fd = new FormData();
                          fd.append('file', file);
                          try {
                            const r = await axios.post(`${API}/sponsors/upload-logo`, fd, {
                              headers: { Authorization: headers.Authorization },
                            });
                            const abs = `${process.env.REACT_APP_BACKEND_URL}${r.data.logo_url}`;
                            setForm(f => ({ ...f, logo_url: abs }));
                            toast.success('Logo uploaded. Save the campaign to persist.');
                          } catch (err) {
                            // Storage may be temporarily unavailable — try base64 fallback so
                            // the admin can still create a campaign without waiting.
                            toast.warning(err.response?.data?.detail || 'Storage upload failed — falling back to inline (base64) logo.');
                            const reader = new FileReader();
                            reader.onload = () => {
                              setForm(f => ({ ...f, logo_url: String(reader.result || '') }));
                              toast.info('Logo attached inline (base64). Save the campaign to persist.');
                            };
                            reader.onerror = () => toast.error('Failed to read image file');
                            reader.readAsDataURL(file);
                          }
                          e.target.value = '';
                        }}
                      />
                      Upload PNG
                    </label>
                    {form.logo_url && (
                      <>
                        <img src={form.logo_url} alt="logo preview" className="w-7 h-7 object-contain border border-stone-200 rounded bg-white" data-testid="sponsor-form-logo-preview" onError={(e) => { e.target.style.display = 'none'; }} />
                        <button
                          type="button"
                          onClick={() => setForm(f => ({ ...f, logo_url: '' }))}
                          className="text-[11px] text-red-600 hover:text-red-800"
                          data-testid="sponsor-form-logo-clear"
                        >Clear</button>
                      </>
                    )}
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Banner URL (optional)</Label>
                  <Input value={form.banner_url || ''} onChange={(e) => setForm(f => ({ ...f, banner_url: e.target.value }))} placeholder="https://…/banner.jpg" data-testid="sponsor-form-banner" />
                </div>
                <div>
                  <Label className="text-xs">CTA Label</Label>
                  <Input value={form.cta_label} onChange={(e) => setForm(f => ({ ...f, cta_label: e.target.value }))} placeholder="View Catalogue" data-testid="sponsor-form-cta-label" />
                </div>
                <div>
                  <Label className="text-xs">CTA URL *</Label>
                  <Input value={form.cta_url} onChange={(e) => setForm(f => ({ ...f, cta_url: e.target.value }))} placeholder="https://www.hafeleindia.com" data-testid="sponsor-form-cta-url" />
                </div>
              </div>

              <div>
                <Label className="text-xs">Placement Zones *</Label>
                <div className="grid grid-cols-3 gap-1 mt-1">
                  {ZONES.map(z => (
                    <label key={z.v} className={`text-xs cursor-pointer flex items-center gap-1.5 border rounded px-2 py-1 ${form.placement_zones.includes(z.v) ? 'bg-amber-50 border-amber-400' : 'border-stone-200 hover:bg-stone-50'}`}>
                      <input type="checkbox" checked={form.placement_zones.includes(z.v)} onChange={() => toggleZone(z.v)} className="h-3 w-3 accent-amber-600" data-testid={`sponsor-form-zone-${z.v}`} />
                      {z.label}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-xs">Category targeting <span className="text-stone-400">(empty = all categories)</span></Label>
                <div className="mt-1 grid grid-cols-1 md:grid-cols-2 gap-1 max-h-56 overflow-y-auto p-2 border border-stone-200 rounded-md bg-stone-50" data-testid="sponsor-form-cat-list">
                  {catCatalog.length === 0 && (
                    <span className="text-xs text-stone-400 italic col-span-2 p-2">Loading categories…</span>
                  )}
                  {catCatalog.map(({ letter, name }) => {
                    const checked = form.category_codes.includes(letter);
                    return (
                      <label
                        key={letter}
                        className={`flex items-center gap-2 px-2 py-1 rounded cursor-pointer text-sm ${checked ? 'bg-amber-50 border border-amber-300' : 'border border-transparent hover:bg-white'}`}
                        data-testid={`sponsor-form-cat-${letter}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleCat(letter)}
                          className="h-3.5 w-3.5 accent-amber-600 flex-shrink-0"
                        />
                        <span className="font-mono font-semibold text-stone-500 w-4">{letter}</span>
                        <span className="text-stone-400">—</span>
                        <span className="text-stone-800 truncate">{name || 'Unnamed'}</span>
                      </label>
                    );
                  })}
                </div>
                {form.category_codes.length > 0 && (
                  <p className="text-[11px] text-amber-700 mt-1">
                    Targeting: {form.category_codes.sort().join(', ')} ({form.category_codes.length} of {catCatalog.length})
                  </p>
                )}
              </div>

              <div className="grid grid-cols-4 gap-2">
                <div>
                  <Label className="text-xs">Start</Label>
                  <Input type="date" value={form.start_date || ''} onChange={(e) => setForm(f => ({ ...f, start_date: e.target.value }))} data-testid="sponsor-form-start" />
                </div>
                <div>
                  <Label className="text-xs">End</Label>
                  <Input type="date" value={form.end_date || ''} onChange={(e) => setForm(f => ({ ...f, end_date: e.target.value }))} data-testid="sponsor-form-end" />
                </div>
                <div>
                  <Label className="text-xs">Package</Label>
                  <Select value={form.package_type} onValueChange={(v) => setForm(f => ({ ...f, package_type: v }))}>
                    <SelectTrigger data-testid="sponsor-form-pkg"><SelectValue /></SelectTrigger>
                    <SelectContent>{PACKAGES.map(p => (<SelectItem key={p} value={p}>{p.replace(/_/g, ' ')}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Weight (0-100)</Label>
                  <Input type="number" value={form.weight} onChange={(e) => setForm(f => ({ ...f, weight: parseInt(e.target.value, 10) || 0 }))} data-testid="sponsor-form-weight" />
                </div>
              </div>

              {/* iter-360b — partnership_kind + exclusivity */}
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <Label className="text-xs">Partnership Kind (client-facing label)</Label>
                  <Select value={form.partnership_kind} onValueChange={(v) => setForm(f => ({ ...f, partnership_kind: v }))}>
                    <SelectTrigger data-testid="sponsor-form-kind"><SelectValue /></SelectTrigger>
                    <SelectContent>{KINDS.map(k => (<SelectItem key={k.v} value={k.v}>{k.label}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <label className="text-xs inline-flex items-center gap-2 cursor-pointer border rounded px-2 py-2 w-full border-amber-300 bg-amber-50">
                    <input type="checkbox" checked={form.is_exclusive} onChange={(e) => setForm(f => ({ ...f, is_exclusive: e.target.checked }))} className="h-3.5 w-3.5 accent-amber-600" data-testid="sponsor-form-exclusive" />
                    Exclusive in these categories
                  </label>
                </div>
              </div>

              <div>
                <Label className="text-xs">Internal notes (optional)</Label>
                <Textarea rows={2} value={form.notes || ''} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} data-testid="sponsor-form-notes" />
              </div>

              <label className="text-xs inline-flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_active} onChange={(e) => setForm(f => ({ ...f, is_active: e.target.checked }))} className="h-3.5 w-3.5 accent-emerald-600" data-testid="sponsor-form-active" />
                Active — placements will render live (subject to Start/End dates)
              </label>
              <p className="text-[10px] text-stone-500 bg-stone-50 border border-stone-200 rounded p-2 flex items-start gap-1.5">
                <BarChart3 size={11} className="mt-0.5 shrink-0 text-stone-400" />
                <span>AI Guardrail: Sponsor data <strong>never</strong> feeds into cost, rate, spec or recommendation prompts. Sponsors are surfaced only through the SponsorBadge component with a clearly-labelled &quot;Sponsored&quot; tag.</span>
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="sponsor-form-cancel">Cancel</Button>
              <Button onClick={save} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="sponsor-form-save">
                {editing ? 'Update Campaign' : 'Create Campaign'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
